<template>
  <v-card
    class="w-[43rem]"
    flat
    :color="$vuetify.theme.currentTheme.navigationColor"
  >
    <div
      class="sticky flex top-0 justify-between z-50 p-4 bg-white dark-bg-custom"
    >
      <h4 class="text-xl">
        {{ editCustomer ? "Edit Customer" : "Add Customer" }}
      </h4>
      <v-btn plain @click="cancel" class="p-4">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="p-4">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-stepper
          v-model="step"
          vertical
          flat
          v-show="jumpToStep"
          color="navigationColor"
        >
          <v-stepper-items>
            <!-- Step 1: Personal Info -->
            <v-stepper-step
              :complete="isPersonalInfoCompleted"
              step="1"
              :rules="[() => stepperRule]"
              editable
              >Personal Info
              <small>{{ stepperRule ? "" : "CRN is required" }}</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <v-text-field
                v-model="crn"
                label="CRN"
                :rules="rules.crn"
                required
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              ></v-text-field>
              <div class="flex justify-between">
                <v-text-field
                  v-model="firstName"
                  label="First Name"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <div class="p-1" />
                <v-text-field
                  v-model="middleName"
                  label="Middle Name"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <div class="p-1" />
                <v-text-field
                  v-model="lastName"
                  label="Last Name"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
              </div>
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                class="checkBorder_a"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    label="Date of birth"
                    placeholder="Date of birth"
                    v-model="dateOfBirthFormat"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    clearable
                    v-on="on"
                    hide-details
                    outlined
                    dense
                    class="mt-3 mb-3"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateOfBirth"
                  :max="$dayjs().format('YYYY-MM-DD')"
                  no-title
                  scrollable
                />
              </v-menu>
              <v-select
                v-model="gender"
                :items="genderTypes"
                item-text="text"
                label="Select Gender"
                item-value="value"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              >
              </v-select>
              <v-text-field
                v-model="customerEmail"
                label="Email Id"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              ></v-text-field>
              <v-combobox
                v-model="customerMobile"
                :items="customerMobile"
                hide-selected
                hint="Press Enter to add"
                label="Mobile Number"
                persistent-hint
                multiple
                small-chips
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              >
                <template v-slot:selection="{ item }">
                  <v-chip small label @click="removeChip(item, customerMobile)">
                    {{ item }}
                    <v-icon small class="ml-2">mdi-close-circle</v-icon>
                  </v-chip>
                </template>
              </v-combobox>
              <div class="flex items-center justify-end">
                <v-btn outlined @click="cancel">Cancel</v-btn>
                <v-btn
                  @click="step++"
                  class="white--text ml-3"
                  :color="$vuetify.theme.currentTheme.primary"
                  >Continue</v-btn
                >
              </div>
            </v-stepper-content>

            <!-- Step 2: Additional Info -->
            <v-stepper-step
              step="2"
              editable
              :complete="isAdditionalInfoCompleted"
              >Additional Info</v-stepper-step
            >
            <v-stepper-content step="2">
              <v-select
                v-model="jobType"
                :items="jobTypes"
                label="Select Job Type"
                item-text="text"
                item-value="value"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              >
              </v-select>
              <v-text-field
                v-model="occupation"
                label="Occupation"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              ></v-text-field>
              <v-text-field
                v-model="company"
                label="Company"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              ></v-text-field>
              <v-text-field
                v-model="salary"
                label="Salary"
                hide-details
                outlined
                dense
                class="mt-3 mb-3"
              ></v-text-field>
              <div class="flex justify-between">
                <v-text-field
                  v-model="fatherName"
                  label="Father Name"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <div class="p-1" />
                <v-text-field
                  v-model="motherName"
                  label="Mother Name"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
              </div>
              <div class="flex items-center justify-end">
                <v-btn @click="step--" outlined>Back</v-btn>
                <v-btn
                  @click="step++"
                  class="white--text ml-3"
                  :color="$vuetify.theme.currentTheme.primary"
                  >Continue</v-btn
                >
              </div>
            </v-stepper-content>

            <!-- Step 3: Address -->
            <v-stepper-step :complete="step > 3" step="3" editable
              >Address</v-stepper-step
            >
            <v-stepper-content step="3">
              <div v-for="(details, index) in customerDetails" :key="index">
                <div class="flex justify-between items-start">
                  <p>{{ `Address ${index + 1}` }}</p>
                  <v-icon
                    size="20"
                    color="red"
                    @click="removeDetails(index)"
                    v-show="customerDetails.length > 1"
                    >mdi-delete</v-icon
                  >
                </div>
                <v-select
                  v-model="details.typeOfAddress"
                  :items="filterAddressTypes(index)"
                  item-text="text"
                  :label="`Address Type`"
                  item-value="value"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                >
                </v-select>
                <v-text-field
                  v-model="details.addressLine1"
                  @click="showAddAddress(index)"
                  :label="`Customer Address`"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
              </div>
              <div class="flex justify-end mb-4">
                <v-btn
                  @click="addCustomerDetails"
                  small
                  outlined
                  v-if="showAddressTypeButton"
                  >Add Address</v-btn
                >
              </div>
              <div class="flex items-center justify-end">
                <v-btn @click="step--" outlined>Back</v-btn>
                <v-btn
                  @click="step++"
                  class="white--text ml-3"
                  :color="$vuetify.theme.currentTheme.primary"
                  >Continue</v-btn
                >
              </div>
            </v-stepper-content>

            <!-- Step 4: References -->
            <v-stepper-step :complete="step > 4" step="4" editable
              >References</v-stepper-step
            >
            <v-stepper-content step="4">
              <!-- References Form Elements -->

              <div v-for="(reference, index) in references" :key="index">
                <div class="flex justify-between items-start">
                  <p>{{ `Reference ${index + 1}` }}</p>
                  <v-icon
                    size="20"
                    color="red"
                    @click="removeReference(index)"
                    v-show="references.length > 1"
                    >mdi-delete</v-icon
                  >
                </div>
                <v-text-field
                  v-model="reference.name"
                  :label="`Name`"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="reference.relation"
                  label="Relation"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="reference.mobile"
                  :label="`Mobile`"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="reference.addresses[0].addressLine1"
                  @click="showAddRefAddress(index)"
                  :label="`Address`"
                  hide-details
                  outlined
                  dense
                  class="mt-3 mb-3"
                ></v-text-field>
              </div>
              <div class="flex justify-end mb-4">
                <v-btn @click="addReference" small outlined
                  >Add Reference</v-btn
                >
              </div>
              <div class="flex items-center justify-end">
                <v-btn @click="step--" outlined>Back</v-btn>

                <v-btn
                  class="white--text ml-3"
                  :color="$vuetify.theme.currentTheme.primary"
                  @click="addCustomer"
                >
                  {{ editCustomer ? "Edit Customer" : "Add Customer" }}
                </v-btn>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-form>
    </div>
    <v-dialog v-model="showAddAddressDialog" width="auto">
      <CompanyAddress
        :company="this.crn"
        :isOpen="showAddAddressDialog"
        @cancel="closeAddressDialogs"
        @save="addLocation"
        title="Add Address"
      />
    </v-dialog>
    <v-dialog v-model="showAddRefDialog" width="auto">
      <CompanyAddress
        :company="this.crn"
        :isOpen="showAddRefDialog"
        @cancel="closeAddressDialogs"
        @save="addRefLocation"
        title="Add Address"
      />
    </v-dialog>
  </v-card>
</template>
<script>
import CompanyAddress from "../CompanyAddress.vue";
export default {
  name: "AddEditCaseTask",
  components: {
    CompanyAddress,
  },
  data() {
    return {
      step: 1,
      valid: true,
      genderTypes: [
        { text: "Male", value: "MALE" },
        { text: "Female", value: "FEMALE" },
        { text: "Others", value: "OTHERS" },
        { text: "Not Applicable", value: "NOT_APPLICABLE" },
      ],
      jobTypes: [
        { text: "Salaried", value: "SALARIED" },
        { text: "Self Employed", value: "SELF_EMPLOYED" },
        { text: "Business", value: "BUSINESS" },
        { text: "Student", value: "STUDENT" },
      ],
      addressTypes: [
        { text: "Current Address", value: "CURRENT_ADDRESS" },
        { text: "Mailing Address", value: "MAILING_ADDRESS" },
        { text: "Billing Address", value: "BILLING_ADDRESS" },
        { text: "Office Address", value: "OFFICE_ADDRESS" },
        { text: "Residential Address", value: "RESIDENTIAL_ADDRESS" },
      ],
      anyReferences: false,
      relation: null,
      showAddAddressDialog: false,
      showAddRefDialog: false,
      customerAddressFormat: "",
      addressTypeToAdd: null,
      selectedAddress: null,
      crn: "",
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirth: null,
      gender: null,
      customerEmail: "",
      menu: false,
      customerMobile: [],
      customerDetails: [
        {
          typeOfAddress: null,
        },
      ],
      references: [
        {
          name: "",
          relation: null,
          mobile: "",
          addresses: [{ addressLine1: "" }],
        },
      ],
      selectedIndex: null,
      jobType: null,
      occupation: "",
      company: "",
      salary: null,
      typeOfAddress: null,
      fatherName: "",
      motherName: "",
      stepperRule: true,
      rules: {
        crn: [(val) => (val || "").length > 0 || `CRN is required`],
      },
    };
  },
  props: {
    editCustomer: Object,
    caseType: String,
  },
  watch: {
    editCustomer: {
      handler(newVal) {
        this.$props.editCustomer = newVal;
        this.populateFormData();
      },
      immediate: true,
    },
  },
  computed: {
    mobileRules() {
      return [
        (v) => (v && v.length === 10) || "Phone Number should have 10 digits",
      ];
    },
    showAddressTypeButton() {
      return this.customerDetails?.length < this.addressTypes.length;
    },
    dateOfBirthFormat() {
      if (this.dateOfBirth) {
        return this.$dayjs(this.dateOfBirth).format("DD-MM-YYYY");
      }
      return this.$dayjs().format("DD-MM-YYYY");
    },
    isPersonalInfoCompleted() {
      return (
        this.crn &&
        this.firstName &&
        this.lastName &&
        this.gender &&
        this.customerEmail &&
        this.customerMobile.length
      );
    },
    isAdditionalInfoCompleted() {
      return (
        this.jobType && this.occupation && this.fatherName && this.motherName
      );
    },
    jumpToStep() {
      return !this.isPersonalInfoCompleted
        ? 1
        : !this.isAdditionalInfoCompleted
        ? 2
        : this.step;
    },
  },
  methods: {
    async addCustomer() {
      try {
        const isValid = this.$refs.form.validate();
        if (!isValid) {
          this.stepperRule = false;
          return;
        }
        const payload = {
          crn: this.crn,
          relatedSubCategories: this.caseType || undefined,
          relatedCategory: "collection",
          firstName: this.firstName || undefined,
          middleName: this.middleName || undefined,
          lastName: this.lastName || undefined,
          customerFullName: `${this.firstName} ${this.middleName} ${this.lastName}`,
          dateOfBirth: this.dateOfBirth || undefined,
          gender: this.gender || undefined,
          customerEmail: this.customerEmail || undefined,
          customerMobile: this.customerMobile || undefined,
          jobType: this.jobType || undefined,
          occupation: this.occupation || undefined,
          company: this.company || undefined,
          salary: this.salary || undefined,
          fatherName: this.fatherName || undefined,
          motherName: this.motherName || undefined,
        };
        if (
          this.customerDetails.length &&
          this.customerDetails[0].addressLine1
        ) {
          payload.customerAddress = this.customerDetails;
        }
        if (this.references.length && this.anyReferences) {
          payload.references = this.references;
        }

        let url = "/workforce/customer";
        let method = "POST";

        if (this.editCustomer) {
          url += `/${this.editCustomer._id}`;
          method = "PUT";
        }

        await this.$axios({
          method: method,
          url: url,
          data: payload,
        });

        this.cancel();
        this.$toast.success(
          `Customer ${this.editCustomer ? "updated" : "added"} successfully`
        );
      } catch (error) {
        this.$toast.error(error.response?.data?.message);
        console.log(error);
      }
    },
    async addLocation(data) {
      const index = this.selectedIndex;
      if (index !== null && index !== undefined) {
        const newAddress = {
          addressLine1: `${data.addLine1}, ${data.addLine2}, ${data.city}, ${data.state}, ${data.pinCode}`,
          addressLine2: data.addLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          pinCode: data.pinCode,
        };
        this.$set(this.customerDetails, index, {
          typeOfAddress: this.customerDetails[index].typeOfAddress,
          ...newAddress,
        });
        this.showAddAddressDialog = false;
      }
    },
    async addRefLocation(data) {
      const index = this.selectedRefIndex;
      const addresses = [];
      if (index !== null && index !== undefined) {
        const newAddress = {
          addressLine1: `${data.addLine1}, ${data.addLine2}, ${data.city}, ${data.state}, ${data.pinCode}`,
          addressLine2: data.addLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          pinCode: data.pinCode,
        };
        addresses.push(newAddress);
        this.$set(this.references, index, {
          name: this.references[index].name,
          relation: this.references[index].relation,
          mobile: this.references[index].mobile,
          addresses: addresses,
        });
        this.showAddRefDialog = false;
      }
    },
    addReference() {
      this.references.push({
        name: "",
        relation: null,
        mobile: "",
        addresses: [{ addressLine1: "" }],
      });
    },
    removeChip(item, options) {
      const index = options.indexOf(item);
      if (index !== -1) {
        options.splice(index, 1);
      }
    },
    filterAddressTypes(index) {
      const selectedTypes = this.customerDetails.map(
        (item) => item.typeOfAddress
      );
      return this.addressTypes.filter(
        (type) =>
          !selectedTypes.includes(type.value) ||
          type.value === this.customerDetails[index].typeOfAddress
      );
    },
    addCustomerDetails() {
      this.customerDetails.push({
        typeOfAddress: null,
      });
    },
    closeAddressDialogs() {
      this.showAddAddressDialog = false;
      this.showAddRefDialog = false;
    },
    removeReference(index) {
      this.references.splice(index, 1);
    },
    removeDetails(index) {
      this.customerDetails.splice(index, 1);
    },
    showAddAddress(index) {
      this.selectedIndex = index;
      this.showAddAddressDialog = true;
    },
    showAddRefAddress(index) {
      this.selectedRefIndex = index;
      this.showAddRefDialog = true;
    },
    cancel() {
      this.$emit("customerAdded");
      this.$refs.form.reset();
      this.customerDetails = [
        {
          typeOfAddress: null,
        },
      ];
      this.step = 1;
      this.stepperRule = true;
    },
    populateFormData() {
      if (this.editCustomer) {
        this.crn = this.editCustomer.crn || "";
        this.firstName = this.editCustomer.firstName || "";
        this.middleName = this.editCustomer.middleName || "";
        this.lastName = this.editCustomer.lastName || "";
        this.dateOfBirth = this.editCustomer.dateOfBirth || null;
        this.gender = this.editCustomer.gender || null;
        this.customerEmail = this.editCustomer.customerEmail || "";
        this.customerMobile = this.editCustomer.customerMobile || "";
        this.jobType = this.editCustomer.jobType || null;
        this.occupation = this.editCustomer.occupation || "";
        this.company = this.editCustomer.company || "";
        this.salary = this.editCustomer.salary || null;
        this.fatherName = this.editCustomer.fatherName || "";
        this.motherName = this.editCustomer.motherName || "";

        // Populate customer addresses
        if (
          this.editCustomer.customerAddress &&
          this.editCustomer.customerAddress.length > 0
        ) {
          this.customerDetails = this.editCustomer.customerAddress.map(
            (address) => ({
              typeOfAddress: address.typeOfAddress || null,
              addressLine1: address.addressLine1 || "",
              addressLine2: address.addressLine2 || "",
              city: address.city || "",
              state: address.state || "",
              country: address.country || "",
              pinCode: address.pinCode || "",
            })
          );
        }

        // Populate references
        if (
          this.editCustomer.references &&
          this.editCustomer.references.length > 0
        ) {
          this.anyReferences = true; // Set to true to show reference fields
          this.references = this.editCustomer.references.map((reference) => ({
            name: reference.name || "",
            relation: reference.relation || null,
            mobile: reference.mobile || "",
            addresses: reference.addresses.map((address) => ({
              addressLine1: address.addressLine1 || "",
              addressLine2: address.addressLine2 || "",
              city: address.city || "",
              state: address.state || "",
              country: address.country || "",
              pinCode: address.pinCode || "",
            })),
          }));
        }
      }
    },
  },
};
</script>
<style></style>
